// @noflow
import { captureException } from '@sentry/browser'
import type { StripeElements } from '@stripe/stripe-js'
import { Stripe } from '@stripe/stripe-js'

import { trackEvent } from '@/services/segment'

import segmentTrack from '@/components/analytics/Analytics'

import { CREATE_PAYMENT_METHOD_MUTATION_STRING } from '../mutations/CreatePaymentMethod'

import { ArchivedType, InputPaymentMethod, Provider } from '@/types'

type CardAuthenticationStatus =
  | 'requires_payment_method'
  | 'requires_confirmation'
  | 'requires_action'
  | 'processing'
  | 'canceled'
  | 'succeeded'

type SCAResponse = {
  card_authentication_setup: {
    client_secret: string
    customer_id: string
    id: string
    status: CardAuthenticationStatus
  }
}

const requestFromSCAController = async (
  email: string,
  csrfToken: string,
  authenticationType: 'card' | 'ideal' | 'bancontact',
  token: string
): Promise<SCAResponse> => {
  const endpoint =
    '/api/payment_methods/credit_cards/strong_customer_authentication/create'

  try {
    const response = await fetch(`${endpoint}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': csrfToken
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        email,
        csrf_token: csrfToken,
        authentication_type: authenticationType,
        token
      })
    })

    if (!response.ok) {
      throw new Error(
        `Error requesting from SCA controller: ${response.statusText}`
      )
    }

    return await response.json()
  } catch (err) {
    throw new Error(
      `Error requesting from SCA controller: ${
        err instanceof Error ? err.message : String(err)
      }`
    )
  }
}

const addBancontactPaymentMethod = (
  email: string,
  csrfToken: string,
  token: string,
  stripe: Stripe,
  elements: StripeElements | null,
  name: string,
  redirectUrl: string,
  successHandler: () => void,
  errorHandler: (arg: string) => void
): Promise<Response> => {
  return new Promise((): void => {
    trackEvent('Creating new Payment method', {
      component_identifier:
        'AddPaymentMethodPage_CreatePaymentMethod_Bancontact_create'
    })
    requestFromSCAController(
      encodeURIComponent(email),
      csrfToken,
      'bancontact',
      token
    ).then((res) => {
      elements?.submit().then(({ error: submitError }) => {
        if (submitError) {
          errorHandler(
            submitError.message || 'Error validating payment details'
          )
          return
        }

        stripe
          .confirmSetup({
            elements,
            clientSecret: res.card_authentication_setup.client_secret,
            redirect: 'if_required',
            confirmParams: {
              return_url: redirectUrl,
              payment_method_data: {
                billing_details: {
                  email,
                  name
                }
              }
            }
          })
          .then((result) => {
            segmentTrack('Bancontact redirection initialised')
            if (result.error) {
              captureException(
                `Error confirming Bancontact Setup in My Details: ${result.error}`
              )
              errorHandler(result.error.message || '')
            } else {
              successHandler()
            }
          })
      })
    })
  })
}

const addIdealPaymentMethod = (
  email: string,
  csrfToken: string,
  token: string,
  stripe: Stripe,
  elements: StripeElements | null,
  name: string,
  redirectUrl: string,
  successHandler: () => void,
  errorHandler: (arg: string) => void
): Promise<Response> => {
  return new Promise((): void => {
    trackEvent('Creating new Payment method', {
      component_identifier:
        'AddPaymentMethodPage_CreatePaymentMethod_Ideal_create'
    })
    requestFromSCAController(
      encodeURIComponent(email),
      csrfToken,
      'ideal',
      token
    ).then((res) => {
      elements?.submit().then(({ error: submitError }) => {
        if (submitError) {
          errorHandler(
            submitError.message || 'Error validating payment details'
          )
          return
        }

        stripe
          .confirmSetup({
            elements,
            clientSecret: res.card_authentication_setup.client_secret,
            redirect: 'if_required',
            confirmParams: {
              return_url: redirectUrl,
              payment_method_data: {
                billing_details: {
                  email,
                  name
                }
              }
            }
          })
          .then((result) => {
            segmentTrack('iDeal redirection initialised')
            if (result.error) {
              captureException(
                `Error confirming Ideal Setup in My Details: ${result.error}`
              )
              errorHandler(result.error.message || '')
            } else {
              successHandler()
            }
          })
      })
    })
  })
}

const updateCreditCard = async (
  stripe: Stripe,
  elements: StripeElements,
  isActive: boolean,
  successHandler: () => void,
  errorHandler: (arg: string) => void,
  userID: number,
  csrfToken: string,
  email: string,
  authenticationType: 'card',
  userToken: string
): Promise<Response> => {
  try {
    if (typeof userID !== 'number') {
      throw new Error(`User ID (${userID}) should be of type number`)
    }

    if (!elements) {
      throw new Error('Could not find PaymentElements at My details page')
    }

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit()
    if (submitError) {
      errorHandler(submitError.message || 'Error validating payment details')
      return Promise.reject(submitError)
    }

    const endpoint = '/graphql'
    const graphQLQuery = CREATE_PAYMENT_METHOD_MUTATION_STRING
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken
    }
    const method = 'POST'
    const credentials = 'same-origin'

    let paymentMethodId: string

    if (email) {
      // If email is provided, we're using SCA flow

      trackEvent('Creating new Payment method', {
        component_identifier:
          'AddPaymentMethodPage_CreatePaymentMethod_CreditCardWithSCA_create'
      })

      const {
        card_authentication_setup: { client_secret: clientSecret }
      } = await requestFromSCAController(
        encodeURIComponent(email),
        csrfToken,
        authenticationType,
        userToken
      )

      const result = await stripe.confirmSetup({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              email
            }
          }
        }
      })

      if (result.error) {
        captureException(
          `Error confirming setup in My Details: ${result.error}`
        )
        errorHandler(result.error.message || 'Error processing payment method')
        return Promise.reject(result.error)
      }

      if (!result.setupIntent?.payment_method) {
        errorHandler('No payment method returned from Stripe')
        return Promise.reject(
          new Error('No payment method returned from Stripe')
        )
      }

      // Set the payment method ID from the setupIntent
      // Handle both cases: when payment_method is a string ID or a PaymentMethod object
      const paymentMethod = result.setupIntent.payment_method
      paymentMethodId =
        typeof paymentMethod === 'string'
          ? paymentMethod
          : (paymentMethod as { id: string }).id
    } else {
      // If email is not provided, we're using non-SCA

      trackEvent('Creating new Payment method', {
        component_identifier:
          'AddPaymentMethodPage_CreatePaymentMethod_CreditCardWithoutSCA_create'
      })

      // For non-SCA flow, create a payment method directly
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements
      })

      if (error) {
        errorHandler(error.message || 'Error creating payment method')
        return Promise.reject(error)
      }

      if (!paymentMethod?.id) {
        errorHandler('No payment method ID returned from Stripe')
        return Promise.reject(
          new Error('No payment method ID returned from Stripe')
        )
      }

      // Set the payment method ID from the createPaymentMethod result
      paymentMethodId = paymentMethod.id
    }

    // At this point we have a paymentMethodId from either flow

    // Send the payment method to the server
    const body = JSON.stringify({
      query: graphQLQuery,
      variables: {
        userId: userID,
        archivedType: ArchivedType.non_archived,
        paymentMethods: [
          {
            paymentMethodId,
            active: isActive,
            paymentMethodType: InputPaymentMethod.credit_card,
            paymentProvider: Provider.stripe
          }
        ]
      }
    })

    const response = await fetch(endpoint, {
      headers,
      method,
      credentials,
      body
    })

    const responseData = await response.json()

    if (!response.ok || responseData.errors) {
      const errorMsg =
        responseData.errors?.[0]?.message || 'Error saving payment method'
      errorHandler(errorMsg)
      return Promise.reject(new Error(errorMsg))
    }

    successHandler()
    return response
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err)
    errorHandler(errorMessage)
    return Promise.reject(err)
  }
}

export { updateCreditCard, addIdealPaymentMethod, addBancontactPaymentMethod }
